import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PricingSyncControls } from '../components/pricing/PricingSyncControls';
import { ManapoolBulkRepricePanel } from '../components/pricing/ManapoolBulkRepricePanel';
import { RepricingJobTable } from '../components/pricing/RepricingJobTable';
import {
  listPricingJobs,
  updatePricingJob,
  PricingJob,
  getReportUrl,
  getExportUrl,
} from '../api/pricing';
import { JobDetailDrawer } from '../components/pricing/JobDetailDrawer';
import { usePricingJob } from '../hooks/usePricingJob';
import { colors, spacing, typography, borderRadius } from '../styles/tokens';

const PricingPage: React.FC = () => {
  const [jobs, setJobs] = useState<PricingJob[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const {
    job: selectedJob,
    error: jobError,
    refetch: refetchJob,
    startPolling,
  } = usePricingJob(selectedJobId, { enabled: Boolean(selectedJobId) });

  const hydratedJob = useMemo(() => {
    if (!selectedJob) return null;
    return {
      ...selectedJob,
      html_report_path: selectedJob.html_report_path
        ? getReportUrl(selectedJob.id, selectedJob.html_report_path)
        : undefined,
      csv_export_path: selectedJob.csv_export_path
        ? getExportUrl(selectedJob.id, selectedJob.csv_export_path)
        : undefined,
    };
  }, [selectedJob]);

  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await listPricingJobs({
        limit,
        offset: (page - 1) * limit,
      });

      setJobs(response.items);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs');
    } finally {
      setIsLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleJobAction = useCallback(
    async (jobId: string, action: string) => {
      setError(null);

      if (action === 'view') {
        setSelectedJobId(jobId);
        startPolling();
        return;
      }

      try {
        const mappedAction = action as 'cancel' | 'pause' | 'resume';
        await updatePricingJob(jobId, mappedAction);
        await fetchJobs();
        if (jobId === selectedJobId) {
          await refetchJob();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update job');
      }
    },
    [fetchJobs, refetchJob, selectedJobId, startPolling]
  );

  const handleJobClick = useCallback(
    (job: PricingJob) => {
      setSelectedJobId(job.id);
      startPolling();
    },
    [startPolling]
  );

  const handleDrawerClose = useCallback(() => setSelectedJobId(null), []);

  const totalPages = Math.ceil(total / limit);

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column' as const,
      minHeight: '100vh',
      backgroundColor: colors.background,
    },
    content: {
      flex: 1,
      padding: spacing.lg,
    },
    header: {
      marginBottom: spacing.xl,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: typography.h1.fontSize,
      fontWeight: typography.h1.fontWeight,
      color: colors.text,
    },
    subtitle: {
      fontSize: typography.small.fontSize,
      color: colors.textSecondary,
      marginTop: spacing.xs,
    },
    error: {
      backgroundColor: colors.error,
      color: 'white',
      padding: spacing.md,
      borderRadius: borderRadius.sm,
      marginBottom: spacing.lg,
      fontSize: typography.body.fontSize,
    },
    section: {
      marginBottom: spacing['2xl'],
    },
    sectionTitle: {
      fontSize: typography.h2.fontSize,
      fontWeight: typography.h2.fontWeight,
      color: colors.text,
      marginBottom: spacing.md,
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      gap: spacing.sm,
      marginTop: spacing.lg,
    },
    paginationButton: {
      padding: `${spacing.xs} ${spacing.sm}`,
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.sm,
      cursor: 'pointer',
      backgroundColor: colors.surface,
      color: colors.text,
      transition: 'all 0.2s ease',
    },
  };

  return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.header}>
            <div>
              <div style={styles.title}>Pricing Management</div>
              <div style={styles.subtitle}>
                Canonical reprice: Manapool bulk-price → SyncBase listing → Shopify
              </div>
            </div>
          </div>
          {(error || jobError) && <div style={styles.error}>{jobError || error}</div>}

          <ManapoolBulkRepricePanel />

          <PricingSyncControls />

          <div style={styles.section}>
            <div style={styles.sectionTitle}>Recent Jobs</div>
            <RepricingJobTable
              jobs={jobs}
              isLoading={isLoading}
              onAction={handleJobAction}
              onJobClick={handleJobClick}
            />

            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={styles.paginationButton}
                  disabled={page === 1}
                  onClick={() => setPage(Math.max(1, page - 1))}
                >
                  ← Previous
                </button>
                <span style={{ ...styles.paginationButton, border: 'none', cursor: 'default' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  style={styles.paginationButton}
                  disabled={page >= totalPages}
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
        <JobDetailDrawer
          job={hydratedJob}
          isOpen={Boolean(selectedJobId)}
          onClose={handleDrawerClose}
          onAction={(jobId, action) => handleJobAction(jobId, action)}
        />
      </div>
  );
};

export default PricingPage;
